/**
 * FASE 22.1 — WORKFLOW RUNTIME
 *
 * CRUD operations on the workflow state.
 * Emits to the workflow timeline on every mutation.
 * Callers (useKeviaChat, etc.) emit RT_EVENTS separately.
 *
 * No kevia-runtime/index.js imports to avoid circular deps.
 * Callers are responsible for emitting RT_EVENTS.
 */

import {
  WORKFLOW_STATUS,
  STEP_STATUS,
  buildInitialSteps,
  computeProgress,
  getWorkflowDefinition,
} from "./workflowSchemas";
import {
  addWorkflow,
  updateWorkflow,
  removeWorkflow,
  getWorkflowById,
  getWorkflows,
} from "./workflowState";
import { pushWorkflowEvent, WF_TIMELINE_EVENTS } from "./workflowTimeline";
import { generateUUID } from "../uuid";

/* ─── Create ─────────────────────────────────────────── */

/**
 * @param {Object} params
 * @param {string} params.type           — WORKFLOW_TYPES
 * @param {string} [params.priority]     — overrides type default
 * @param {Array}  [params.related_entities]
 * @param {string} [params.next_recommended_action]
 * @returns {Workflow | null}
 */
export function createWorkflow({ type, priority, related_entities = [], next_recommended_action = null }) {
  const def = getWorkflowDefinition(type);
  if (!def) return null;

  const now = Date.now();
  const wf  = {
    id:                     generateUUID(),
    type,
    status:                 WORKFLOW_STATUS.ACTIVE,
    priority:               priority ?? def.defaultPriority,
    created_at:             now,
    updated_at:             now,
    related_entities:       related_entities.slice(0, 3),
    steps:                  buildInitialSteps(type),
    progress:               0,
    last_activity:          now,
    next_recommended_action,
  };

  const added = addWorkflow(wf);
  if (!added) return null;

  pushWorkflowEvent({ workflowId: wf.id, type: WF_TIMELINE_EVENTS.CREATED, meta: { wfType: type } });
  return wf;
}

/* ─── Update ─────────────────────────────────────────── */

export function advanceWorkflowStep(workflowId, stepId) {
  const wf = getWorkflowById(workflowId);
  if (!wf) return false;

  const newSteps = wf.steps.map((s) =>
    s.id === stepId ? { ...s, status: STEP_STATUS.DONE, completed_at: Date.now() } : s,
  );
  const ok = updateWorkflow(workflowId, {
    steps:    newSteps,
    progress: computeProgress(newSteps),
    next_recommended_action: null, // will be re-resolved next time
  });
  if (ok) pushWorkflowEvent({ workflowId, type: WF_TIMELINE_EVENTS.STEP_DONE, meta: { stepId } });
  return ok;
}

export function pauseWorkflow(workflowId) {
  const ok = updateWorkflow(workflowId, { status: WORKFLOW_STATUS.PAUSED });
  if (ok) pushWorkflowEvent({ workflowId, type: WF_TIMELINE_EVENTS.PAUSED });
  return ok;
}

export function resumeWorkflow(workflowId) {
  const ok = updateWorkflow(workflowId, { status: WORKFLOW_STATUS.ACTIVE });
  if (ok) pushWorkflowEvent({ workflowId, type: WF_TIMELINE_EVENTS.RESUMED });
  return ok;
}

export function completeWorkflow(workflowId) {
  const wf       = getWorkflowById(workflowId);
  const newSteps = wf?.steps?.map((s) =>
    s.status === STEP_STATUS.PENDING || s.status === STEP_STATUS.IN_PROGRESS
      ? { ...s, status: STEP_STATUS.DONE, completed_at: Date.now() }
      : s,
  ) ?? [];
  const ok = updateWorkflow(workflowId, {
    status:   WORKFLOW_STATUS.COMPLETED,
    steps:    newSteps,
    progress: 1,
    next_recommended_action: null,
  });
  if (ok) pushWorkflowEvent({ workflowId, type: WF_TIMELINE_EVENTS.COMPLETED });
  return ok;
}

export function archiveWorkflow(workflowId) {
  const ok = updateWorkflow(workflowId, { status: WORKFLOW_STATUS.ARCHIVED });
  if (ok) pushWorkflowEvent({ workflowId, type: WF_TIMELINE_EVENTS.ARCHIVED });
  return ok;
}

export function setWorkflowNextAction(workflowId, actionLabel) {
  return updateWorkflow(workflowId, { next_recommended_action: actionLabel });
}

/** Upsert — if a workflow of this type for this entity exists, return it. Else create. */
export function getOrCreateWorkflow({ type, related_entities = [], priority }) {
  const entityId = related_entities[0]?.id;
  if (entityId) {
    const existing = getWorkflows().find(
      (w) =>
        w.type === type &&
        (w.status === WORKFLOW_STATUS.ACTIVE || w.status === WORKFLOW_STATUS.PAUSED) &&
        w.related_entities.some((e) => e.id === entityId),
    );
    if (existing) return existing;
  }
  return createWorkflow({ type, related_entities, priority });
}
